import type { MetricColumnConfig } from '../metrics';
import type { MetricConfig, MetricGroupConfig } from '../../types/config';
import { ConfigValidationError } from '../schema/customerConfigSchema';

/**
 * Backend group keys mapped to the two EDITABLE FE metric clusters (the PromoTab
 * grid). Split on the stable group KEY (not the display string) so a renamed
 * `display_name` cannot misroute columns. Any other DECLARED group (e.g.
 * `kpi_metrics`) is report-only and intentionally excluded from the editable
 * grid — it is surfaced through the `metric_views` report adapter instead.
 */
export const PROMO_GROUP_KEYS = new Set<string>(['promo_metrics']);
export const SPEND_GROUP_KEYS = new Set<string>(['spend_metrics']);

export interface AdaptedMetrics {
    promoMetricsConfig: MetricColumnConfig[];
    promoSpendsConfig: MetricColumnConfig[];
    /** `metric_groups[].display_name` for the promo_metrics group (Metrics tab label). */
    promoMetricsGroupLabel: string;
    /** `metric_groups[].display_name` for the spend_metrics group (Spends tab label). */
    promoSpendsGroupLabel: string;
}

const FALLBACK_PROMO_GROUP_LABEL = 'Metrics';
const FALLBACK_SPEND_GROUP_LABEL = 'Spends';

function resolveGroupDisplayName(
    groups: MetricGroupConfig[],
    keys: Set<string>,
    fallback: string,
): string {
    return groups.find((g) => keys.has(g.key))?.display_name ?? fallback;
}

const toColumn = (m: MetricConfig, groupName: string): MetricColumnConfig => {
    const formula = m.fe_formula ?? m.formula;
    return {
        key: m.key,
        title: m.display_name,
        editable: m.editable,
        allowedDecimals: m.precision,
        minValue: m.min_value ?? null,
        maxValue: m.max_value ?? null,
        preUnit: m.currency_prefix ?? '',
        postUnit: m.currency_suffix ?? '',
        groupName,
        order: m.order,
        defaultVisible: m.default_visible,
        ...(formula ? { formula } : {}),
        ...(m.depends_on ? { dependencies: m.depends_on } : {}),
    };
};

/**
 * Maps backend `metrics[]` to the FE editable grid `MetricColumnConfig`s, split
 * into the promo / spend clusters by group. Fails loud (throws
 * `ConfigValidationError`) only when a metric references a group that is NOT
 * declared in `metric_groups` (referential-integrity violation). Metrics in
 * other declared, non-editable groups (e.g. `kpi_metrics`) are report-only and
 * deliberately excluded from the editable grid (no orphan error).
 */
export function adaptMetrics(metrics: MetricConfig[], groups: MetricGroupConfig[]): AdaptedMetrics {
    const groupByKey = new Map(groups.map((g) => [g.key, g]));

    const unknownGroup = metrics.filter((m) => !groupByKey.has(m.group));
    if (unknownGroup.length) {
        throw new ConfigValidationError(
            unknownGroup.map((m) => ({
                path: ['metrics', m.key, 'group'],
                message: `references unknown group "${m.group}"`,
            })),
        );
    }

    const promoMetricsConfig: MetricColumnConfig[] = [];
    const promoSpendsConfig: MetricColumnConfig[] = [];

    for (const m of metrics) {
        if (PROMO_GROUP_KEYS.has(m.group)) {
            promoMetricsConfig.push(toColumn(m, groupByKey.get(m.group)!.display_name));
        } else if (SPEND_GROUP_KEYS.has(m.group)) {
            promoSpendsConfig.push(toColumn(m, groupByKey.get(m.group)!.display_name));
        }
        // Other declared groups (kpi_metrics, future report-only groups) are
        // intentionally skipped here; `kpi_metrics` feeds the MetricPage KPI
        // catalog via `kpiCatalogAdapter`.
    }

    return {
        promoMetricsConfig,
        promoSpendsConfig,
        promoMetricsGroupLabel: resolveGroupDisplayName(groups, PROMO_GROUP_KEYS, FALLBACK_PROMO_GROUP_LABEL),
        promoSpendsGroupLabel: resolveGroupDisplayName(groups, SPEND_GROUP_KEYS, FALLBACK_SPEND_GROUP_LABEL),
    };
}
